import { keyframes } from '@emotion/react';
import { Typography, styled } from '@ori-ui/mui';
import useTestId from '@ori/testid-generator';
import type { VFC } from 'react';
import { useEffect, useState } from 'react';

const growShrinkAnimation = keyframes`
  50% {
    transform: scale(1.2);
  }
`;

const ShoppingBagCounterRoot = styled(Typography)<{
  ownerState: { isAnimationActive: boolean };
}>(({ theme, ownerState }) => ({
  alignItems: 'center',
  backgroundColor: theme.palette.primary.main,
  border: `1px solid ${theme.palette.common.white}`,
  borderRadius: '50%',
  bottom: theme.spacing(2),
  color: theme.palette.common.white,
  display: 'flex',
  fontSize: theme.typography.pxToRem(10),
  fontWeight: theme.typography.fontWeightBold,
  height: 22,
  justifyContent: 'center',
  left: theme.spacing(3),
  letterSpacing: 0,
  position: 'absolute',
  width: 22,

  ...(ownerState.isAnimationActive && {
    animationDuration: '1s',
    animationName: `${growShrinkAnimation}`,
  }),
}));

const MINIBAG_COUNTER_ANIMATION_TIMEOUT = 1000;

interface ShoppingBagCounterProps {
  shoppingBagQuantity: number;
}

export const ShoppingBagCounter: VFC<ShoppingBagCounterProps> = ({ shoppingBagQuantity }) => {
  const [isAnimationActive, setIsAnimationActive] = useState(false);
  const ownerState = { isAnimationActive };
  const { getTestId } = useTestId();

  useEffect(() => setIsAnimationActive(true), [shoppingBagQuantity]);

  useEffect(() => {
    if (isAnimationActive) {
      setTimeout(() => setIsAnimationActive(false), MINIBAG_COUNTER_ANIMATION_TIMEOUT);
    }
  }, [isAnimationActive]);

  return (
    <ShoppingBagCounterRoot
      ownerState={ownerState}
      data-testid={getTestId('shopping-bag-counter-root')}
    >
      {shoppingBagQuantity}
    </ShoppingBagCounterRoot>
  );
};
